import type { Dictionary, Translation } from '@/i18n/types';

const ro = {
  loading: 'Se încarcă…',
  notFound: 'Pagina căutată nu există.',
  backToCommunity: 'Înapoi la comunitate',
  privateTitle: 'Spațiu privat',
  privateBody: 'Acest spațiu este vizibil doar membrilor săi. Autentificați-vă cu contul potrivit.',
  badgePublic: 'Forum public',
  badgePrivate: 'Spațiu privat',
  topicsHeading: 'Subiecte',
  noTopics: 'Niciun subiect deschis încă.',
  topicReplyCount: '{{count}} răspunsuri',
  topicReplyCount_one: '{{count}} răspuns',
  topicReplyCount_other: '{{count}} de răspunsuri',
  topicLastActivity: 'ultimul {{when}}',
  newTopicPlaceholder: 'Titlul unui subiect nou',
  newTopicAction: 'Deschide subiectul',
  signInToParticipate: 'Autentificare pentru a participa',
  membersHeading: 'Membri',
  ownerTag: 'proprietar',
  memberSince: 'în spațiu din {{date}}',
  invite: 'Invită',
  inviteTitle: 'Invită în spațiu',
  inviteLead: 'Dați codul colegilor; ei intră din Comunitatea mea → Alătură-te cu un cod.',
  accessCodeLabel: 'Codul de acces',
  accessCodeLoading: 'Se citește codul…',
  copyCode: 'Copiază',
  codeCopied: 'Codul a fost copiat.',
  copyFailed: 'Codul nu a putut fi copiat; selectați-l și copiați-l de mână.',
  renewCode: 'Generează alt cod',
  renewConfirmTitle: 'Generați alt cod?',
  renewConfirmBody:
    'Codul de acum nu va mai permite intrarea în spațiu. Colegii care nu au intrat încă au ' +
    'nevoie de codul nou.',
  codeRenewed: 'Codul a fost înlocuit; cel vechi nu mai permite intrarea.',
  codeError: 'Codul nu a putut fi citit.',
  renewError: 'Codul nu a putut fi înlocuit.',
  closeInvite: 'Închide',
  closeDialog: 'Închide fereastra',
  removeMember: 'Scoate',
  removeMemberAria: 'Scoate pe {{name}} din spațiu',
  removeConfirmTitle: 'Scoateți membrul din spațiu?',
  removeConfirmBody:
    '{{name}} nu va mai vedea subiectele spațiului. Poate reveni doar cu un cod de acces.',
  memberRemoved: '{{name}} a fost scos din spațiu.',
  removeError: 'Membrul nu a putut fi scos.',
  leaveSpace: 'Părăsește spațiul',
  leaveConfirmTitle: 'Părăsiți spațiul?',
  leaveConfirmBody:
    'Nu veți mai vedea subiectele spațiului. Puteți reveni doar cu un cod de acces de la ' +
    'proprietar.',
  leaveError: 'Nu ați putut părăsi spațiul.',
  backToForum: 'Înapoi la forum',
  topicHeading: 'Subiect',
  noPosts: 'Niciun răspuns încă.',
  replyPlaceholder: 'Scrieți un răspuns…',
  replyAction: 'Trimite',
  replySubmitted: 'Răspuns trimis.',
  pendingHeading: 'Postări în așteptarea moderării',
  noPending: 'Nimic în așteptare.',
  approve: 'Aprobă',
  reject: 'Respinge',
  noQuestions: 'Nicio întrebare încă.',
  awaitingAnswer: 'În așteptarea unui răspuns din partea expertului.',
  questionPlaceholder: 'Scrieți întrebarea dumneavoastră…',
  askAction: 'Trimite întrebarea',
  signInToAsk: 'Autentificare pentru a întreba',
  questionSubmitted: 'Întrebare trimisă.',
  answerPlaceholder: 'Scrieți răspunsul expertului…',
  answerAction: 'Răspunde',
  answerSubmitted: 'Răspuns înregistrat.',
  answerError: 'Doar expertul sesiunii poate răspunde.',
  associationBadge: 'Asociație profesională',
  associationNotFound: 'Asociația căutată nu există sau nu mai este publică.',
  associationFiscalCode: 'Cod fiscal: {{code}}',
} as const;

const en = {
  loading: 'Loading…',
  notFound: 'This page does not exist.',
  backToCommunity: 'Back to community',
  privateTitle: 'Private space',
  privateBody: 'This space is visible only to its members. Sign in with the right account.',
  badgePublic: 'Public forum',
  badgePrivate: 'Private space',
  topicsHeading: 'Topics',
  noTopics: 'No topic opened yet.',
  topicReplyCount: '{{count}} replies',
  topicReplyCount_one: '{{count}} reply',
  topicLastActivity: 'last {{when}}',
  newTopicPlaceholder: 'Title for a new topic',
  newTopicAction: 'Open topic',
  signInToParticipate: 'Sign in to participate',
  membersHeading: 'Members',
  ownerTag: 'owner',
  memberSince: 'member since {{date}}',
  invite: 'Invite',
  inviteTitle: 'Invite to the space',
  inviteLead: 'Give the code to your colleagues; they join from My community → Join with a code.',
  accessCodeLabel: 'Access code',
  accessCodeLoading: 'Reading the code…',
  copyCode: 'Copy',
  codeCopied: 'The code was copied.',
  copyFailed: 'The code could not be copied; select it and copy it by hand.',
  renewCode: 'Generate another code',
  renewConfirmTitle: 'Generate another code?',
  renewConfirmBody:
    'The current code will no longer let anyone into the space. Colleagues who have not joined ' +
    'yet need the new code.',
  codeRenewed: 'The code was replaced; the old one no longer lets anyone in.',
  codeError: 'The code could not be read.',
  renewError: 'The code could not be replaced.',
  closeInvite: 'Close',
  closeDialog: 'Close the window',
  removeMember: 'Remove',
  removeMemberAria: 'Remove {{name}} from the space',
  removeConfirmTitle: 'Remove the member from the space?',
  removeConfirmBody:
    '{{name}} will no longer see the topics of the space, and can only come back with an access ' +
    'code.',
  memberRemoved: '{{name}} was removed from the space.',
  removeError: 'The member could not be removed.',
  leaveSpace: 'Leave the space',
  leaveConfirmTitle: 'Leave the space?',
  leaveConfirmBody:
    'You will no longer see the topics of the space, and can only come back with an access code ' +
    'from its owner.',
  leaveError: 'You could not leave the space.',
  backToForum: 'Back to forum',
  topicHeading: 'Topic',
  noPosts: 'No replies yet.',
  replyPlaceholder: 'Write a reply…',
  replyAction: 'Send',
  replySubmitted: 'Reply sent.',
  pendingHeading: 'Posts awaiting moderation',
  noPending: 'Nothing pending.',
  approve: 'Approve',
  reject: 'Reject',
  noQuestions: 'No question yet.',
  awaitingAnswer: 'Awaiting an answer from the expert.',
  questionPlaceholder: 'Write your question…',
  askAction: 'Ask',
  signInToAsk: 'Sign in to ask',
  questionSubmitted: 'Question sent.',
  answerPlaceholder: 'Write the expert’s answer…',
  answerAction: 'Answer',
  answerSubmitted: 'Answer recorded.',
  answerError: 'Only the session expert may answer.',
  associationBadge: 'Professional association',
  associationNotFound: 'This association does not exist, or is no longer public.',
  associationFiscalCode: 'Fiscal code: {{code}}',
} satisfies Translation<keyof typeof ro>;

export const publicCommunity = { ro, en } satisfies Dictionary;

export type PublicCommunityKey = keyof typeof ro;
